import { ChevronDown, Circle, Square } from 'lucide-react';
import { SURVEY_QUESTION_TYPE, type SurveyQuestionTypeValue } from '../surveyTypes';

/** What the preview needs of one question — the form's own draft, read as it is typed. */
export interface PreviewQuestion {
  key: string;
  text: string;
  description: string;
  type: SurveyQuestionTypeValue;
  isRequired: boolean;
  scaleMin: number;
  scaleMax: number;
  scaleMinLabel: string;
  scaleMaxLabel: string;
  options: { key: string; text: string }[];
}

const FIELD =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-400';

/**
 * The questionnaire as a respondent will see it — the right half of the questionnaire form.
 *
 * **It is a picture, not a form.** The whole region is `inert`, so none of its sample controls
 * can be focused, clicked or reached by Tab: the run must go through the real fields to the
 * primary action and nothing else. It renders straight from the form's draft, so it follows
 * every keystroke without saving anything.
 */
export function SurveyQuestionnairePreview({
  title,
  description,
  questions,
  heading = 'Preview',
}: {
  title: string;
  description: string;
  questions: PreviewQuestion[];
  /** The label over it — the form's "Preview"; null where the surface already names it. */
  heading?: string | null;
}) {
  return (
    <section aria-label={heading ?? title} className="flex flex-col gap-4">
      {heading ? <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{heading}</p> : null}

      <div inert className="flex flex-col gap-3">
        <div className="rounded-xl border border-slate-200 border-t-4 border-t-brand-500 bg-white p-5">
          <p className="text-lg text-slate-800">{title.trim() || <span className="text-slate-400">Title</span>}</p>
          {description.trim() ? (
            <p className="mt-2 whitespace-pre-line text-sm text-slate-600">{description}</p>
          ) : null}
        </div>

        {questions.length === 0 ? (
          <p className="px-1 text-xs text-slate-500">The questions appear here as they are added.</p>
        ) : null}

        {questions.map((question, index) => (
          <div key={question.key} className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5">
            {/*
              The number leads, in mono so a column of them lines up. The question and its
              description share the column beside it, so both align with the text however wide
              the number grows. It is the block's position, so moving a question renumbers.
            */}
            <div className="flex gap-2">
              <span className="shrink-0 font-mono text-sm tabular-nums text-slate-500">{index + 1}.</span>
              <div className="flex min-w-0 flex-col gap-1">
                <p className="text-sm text-slate-800">
                  {question.text.trim() || <span className="text-slate-400">Question</span>}
                  {question.isRequired ? <span className="ml-1 text-red-600">*</span> : null}
                </p>
                {/* Under the question and a step smaller, as the product owner asked. */}
                {question.description.trim() ? (
                  <p className="whitespace-pre-line text-xs text-slate-500">{question.description}</p>
                ) : null}
              </div>
            </div>

            <AnswerPreview question={question} />
          </div>
        ))}
      </div>
    </section>
  );
}

/** The control a respondent answers with, drawn for the question's type. */
function AnswerPreview({ question }: { question: PreviewQuestion }) {
  const options = question.options.filter((o) => o.text.trim());

  switch (question.type) {
    case SURVEY_QUESTION_TYPE.MultipleChoice:
    case SURVEY_QUESTION_TYPE.Checkbox: {
      const Glyph = question.type === SURVEY_QUESTION_TYPE.Checkbox ? Square : Circle;

      return (
        <ul className="flex flex-col gap-2">
          {options.map((option) => (
            <li key={option.key} className="flex items-center gap-3 text-sm text-slate-600">
              <Glyph className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={1.5} aria-hidden="true" />
              {option.text}
            </li>
          ))}
        </ul>
      );
    }

    case SURVEY_QUESTION_TYPE.Dropdown:
      return (
        <div className={`${FIELD} flex max-w-xs items-center justify-between`}>
          Choose
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </div>
      );

    case SURVEY_QUESTION_TYPE.ShortParagraph:
      return <div className={`${FIELD} max-w-md`}>Your answer</div>;

    case SURVEY_QUESTION_TYPE.LongParagraph:
      return <div className={`${FIELD} h-20`}>Your answer</div>;

    case SURVEY_QUESTION_TYPE.Scale: {
      const steps = Array.from({ length: question.scaleMax - question.scaleMin + 1 }, (_, i) => question.scaleMin + i);

      return (
        <div className="flex flex-wrap items-end gap-3 text-sm text-slate-600">
          {question.scaleMinLabel.trim() ? <span className="pb-0.5">{question.scaleMinLabel}</span> : null}
          {steps.map((n) => (
            <span key={n} className="flex flex-col items-center gap-1">
              <span className="font-mono text-xs tabular-nums">{n}</span>
              <Circle className="h-4 w-4 text-slate-400" strokeWidth={1.5} aria-hidden="true" />
            </span>
          ))}
          {question.scaleMaxLabel.trim() ? <span className="pb-0.5">{question.scaleMaxLabel}</span> : null}
        </div>
      );
    }

    case SURVEY_QUESTION_TYPE.Date:
      return <input type="date" disabled aria-hidden="true" className={`${FIELD} max-w-xs font-mono`} />;

    case SURVEY_QUESTION_TYPE.Time:
      return <input type="time" disabled aria-hidden="true" className={`${FIELD} max-w-xs font-mono`} />;

    // Filled from the respondent's own employee record — nothing for them to type.
    case SURVEY_QUESTION_TYPE.Name:
      return <div className={`${FIELD} max-w-md`}>The respondent&apos;s name</div>;

    // A sector field follows when the chosen division has units beneath it.
    case SURVEY_QUESTION_TYPE.Division:
      return (
        <div className={`${FIELD} flex max-w-xs items-center justify-between`}>
          Choose a division
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </div>
      );

    default:
      return null;
  }
}
